/**
 * WP8 · BK-Detail — Wrapper-Page mit sticky Sidebar + Tab-Inhalt.
 *
 * Analog zu `pflege/PbDetailPage.tsx`, aber OHNE Phasen-Elemente
 * (PbPhaseBar/Dashboard/Stundenplan sind PB-spezifisch). Die Tabs aus
 * `pflege/tabs/` sind user-generisch (arbeiten auf /admin/user/{id}/*)
 * und werden 1:1 wiederverwendet.
 *
 * Tabs:
 *   dashboard         — BkDashboard (feste Widgets, Default)
 *   profildaten       — BkProfileEdit (BK-Sections-Formular)
 *   betreuungen       — Einsätze (BetreuungenTab, cares + travels)
 *   medien-dokumente  — MedienTab
 *   kommunikation     — KommunikationTab
 *   aufgaben-notizen  — AufgabenNotizenTab
 *   bewertungen       — BewertungenTab
 *
 * Die Page lädt den BK selbst über GET /v1/admin/user/{id} (v1Users.get),
 * damit App.tsx nur die Route durchreichen muss (additiver Einbau).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui';
import { BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../lib/theme';
import { buildPath } from '../../lib/route';
import { AppLink, isNewTabClick } from '../../lib/openLink';
import { useIsMobile } from '../../lib/useBreakpoint';
import { usePermissions } from '../../contexts/PermissionsContext';
import { v1Users, primaryProfile, type V1User } from '../../lib/v1';
import { AnfrageAbbruchControl } from '../anfragen/AnfrageAbbruchDialog';
import { BkSidebar } from './profile/BkSidebar';
import { BkProfileEdit } from './profile/BkProfileEdit';
import { BetreuungenTab } from '../pflege/tabs/BetreuungenTab';
import { MedienTab } from '../pflege/tabs/MedienTab';
import { KommunikationTab } from '../pflege/tabs/KommunikationTab';
import { ProtokollTab } from '../pflege/tabs/ProtokollTab';
import { AufgabenNotizenTab } from '../pflege/tabs/AufgabenNotizenTab';
import { BewertungenTab } from '../pflege/tabs/BewertungenTab';
import { BkDashboard } from './BkDashboard';
import { useNavSlots } from '../../components/shell/ProfileNavContext';

export type BkTabId =
  | 'dashboard'
  | 'profildaten'
  | 'betreuungen'
  | 'medien-dokumente'
  | 'kommunikation'
  | 'aufgaben-notizen'
  | 'bewertungen'
  | 'protokoll';

// Tab-Labels sind mit den PB-Tabs identisch → pflege.detail.tabs.* wiederverwenden.
export const BK_TABS: { id: BkTabId; labelKey: string }[] = [
  { id: 'dashboard',        labelKey: 'pflege.detail.tabs.overview' },
  { id: 'profildaten',      labelKey: 'pflege.detail.tabs.profile' },
  { id: 'betreuungen',      labelKey: 'pflege.detail.tabs.cares' },
  { id: 'medien-dokumente', labelKey: 'pflege.detail.tabs.media' },
  { id: 'kommunikation',    labelKey: 'pflege.detail.tabs.communication' },
  { id: 'aufgaben-notizen', labelKey: 'pflege.detail.tabs.tasks' },
  { id: 'bewertungen',      labelKey: 'pflege.detail.tabs.ratings' },
];

/** Protokoll-Tab nur mit `protokoll.read`; bewusst nicht Teil von BK_TABS. */
const PROTOKOLL_TAB: { id: BkTabId; labelKey: string } = { id: 'protokoll', labelKey: 'pflege.detail.tabs.protokoll' };
const PROTOKOLL_PERMISSION = 'protokoll.read';

const VALID_TABS = new Set<string>([...BK_TABS.map(t => t.id), PROTOKOLL_TAB.id]);

interface Props {
  /** Numerische User-Id aus der Route /betreuung/{id}. */
  userId: number;
  /** Aktiver Tab-Slug aus der URL — Unbekanntes kollabiert auf 'profildaten'. */
  tab: string | null;
  onClose: () => void;
  onSelectTab: (tab: BkTabId) => void;
  dark: boolean;
}

export function BkDetailPage({ userId, tab, onClose, onSelectTab, dark }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  const { hasPermission } = usePermissions();
  const canReadProtokoll = hasPermission(PROTOKOLL_PERMISSION);
  const activeTab: BkTabId = tab && VALID_TABS.has(tab) && (tab !== 'protokoll' || canReadProtokoll) ? (tab as BkTabId) : 'dashboard';

  const [user, setUser] = useState<V1User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    v1Users.get(userId)
      .then(u => { if (alive) { setUser(u); setLoading(false); } })
      .catch(e => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : tr('betreuung.error.load'));
        setLoading(false);
      });
    return () => { alive = false; };
  }, [userId]);

  // Zurück-Button in die globale Navbar heben (wie Familienkonto).
  useNavSlots(
    {
      leading: (
        // Handy: nur das Chevron, sonst überlappt der Text die Navbar-Schalter.
        <button type="button" onClick={onClose} aria-label={tr('pflege.detail.back')} title={tr('pflege.detail.back')} style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexShrink: 0,
          background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 8,
          padding: isMobile ? 0 : '6px 12px', width: isMobile ? 38 : undefined, height: isMobile ? 38 : undefined,
          cursor: 'pointer', color: t.text, whiteSpace: 'nowrap',
          fontSize: 13, fontFamily: 'Outfit,sans-serif',
        }}>
          <span aria-hidden="true" style={{ fontSize: isMobile ? 20 : 16, lineHeight: 1 }}>‹</span>
          {!isMobile && <> {tr('pflege.detail.back')}</>}
        </button>
      ),
    },
    [dark, isMobile],
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: isMobile ? 'auto' : '100%',
      minHeight: 0,
    }}>

      {error && (
        <Card dark={dark} style={{ padding: 12, marginBottom: 12, borderColor: '#f04438' }}>
          <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>
            {tr('betreuung.error.load')}: {error}
          </div>
        </Card>
      )}

      {loading && !user && !error && (
        <div style={{ fontSize: 12, color: t.muted, padding: 14 }}>{tr('betreuung.list.loading')}</div>
      )}

      {!loading && !error && (
        <div style={{
          flex: isMobile ? undefined : 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 320px) minmax(0, 1fr)',
          gap: isMobile ? 14 : 24,
        }}>

          {/* Links: sticky Sidebar (Block auf Mobile) */}
          <aside style={{ overflowY: isMobile ? 'visible' : 'auto', minWidth: 0 }}>
            <BkSidebar user={user} dark={dark} />
          </aside>

          {/* Rechts: Tab-Bar + Body. Neben der Tab-Bar: ⋯-Menü "Anfrage
              abbrechen" (BK-Kontoansicht) bzw. Badge bei negativer Phase. */}
          <section style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <BkTabBar active={activeTab} onChange={onSelectTab} dark={dark} isMobile={isMobile} userId={userId} />
              </div>
              {user?.id != null && (
                <AnfrageAbbruchControl
                  dark={dark}
                  userId={user.id}
                  scope="bk"
                  userName={user.fullname ?? [primaryProfile(user)?.vorname, primaryProfile(user)?.nachname].filter(Boolean).join(' ')}
                  phase={typeof user.phase === 'number' ? user.phase : null}
                  onCancelled={r => setUser(u => (u ? { ...u, phase: r.phase } as V1User : u))}
                />
              )}
            </div>
            <div style={{ flex: 1, minHeight: 0, marginTop: isMobile ? 12 : 14, display: 'flex', flexDirection: 'column', overflowY: isMobile ? 'visible' : 'auto' }}>
              {activeTab === 'dashboard' && (
                <BkDashboard user={user} dark={dark} onSelectTab={onSelectTab} />
              )}
              {activeTab === 'profildaten' && (
                <BkProfileEdit
                  user={user}
                  dark={dark}
                  onClose={onClose}
                  onSaved={u => setUser(u)}
                  embedded
                />
              )}
              {activeTab === 'betreuungen' && (
                <BetreuungenTab user={user} dark={dark} />
              )}
              {activeTab === 'medien-dokumente' && (
                <MedienTab user={user} dark={dark} />
              )}
              {activeTab === 'kommunikation' && (
                <KommunikationTab user={user} dark={dark} />
              )}
              {activeTab === 'aufgaben-notizen' && (
                <AufgabenNotizenTab user={user} dark={dark} />
              )}
              {activeTab === 'bewertungen' && (
                <BewertungenTab user={user} dark={dark} />
              )}
              {activeTab === 'protokoll' && canReadProtokoll && (
                <ProtokollTab user={user} dark={dark} />
              )}
            </div>
          </section>

        </div>
      )}
    </div>
  );
}

/* ─── Pill-Tab-Bar (Muster PbDetailPage.TabBar, ohne Dashboard-Logik) ────── */
function BkTabBar({
  active, onChange, dark, isMobile = false, userId,
}: {
  userId: number;
  active: BkTabId;
  onChange: (tab: BkTabId) => void;
  dark: boolean;
  isMobile?: boolean;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { hasPermission } = usePermissions();
  const tabs = hasPermission(PROTOKOLL_PERMISSION) ? [...BK_TABS, PROTOKOLL_TAB] : BK_TABS;
  return (
    <div style={{
      display: 'flex',
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      overflowX: isMobile ? 'auto' : 'visible',
      WebkitOverflowScrolling: 'touch',
      gap: 6, padding: 4,
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
      boxShadow: dark ? 'none' : '0 1px 2px rgba(16,24,40,0.05)',
      scrollbarWidth: 'thin',
    }}>
      {tabs.map(({ id, labelKey }) => {
        const on = active === id;
        return (
          <AppLink
            key={id}
            to={buildPath('betreuung', String(userId), id)}
            onClick={(e) => { if (!isNewTabClick(e)) { e.preventDefault(); onChange(id); } }}
            aria-current={on ? 'page' : undefined}
            style={{
              border: 'none', cursor: 'pointer',
              padding: isMobile ? '9px 14px' : '8px 14px', borderRadius: 8,
              background: on ? BRAND_LIGHT : 'transparent',
              color: on ? BRAND_DARK : t.text,
              fontSize: 13, fontWeight: on ? 600 : 500,
              fontFamily: 'Outfit,sans-serif',
              transition: 'background 0.12s',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              minHeight: isMobile ? 38 : undefined,
              textDecoration: 'none', display: 'inline-block',
            }}
            onMouseEnter={e => { if (!on) (e.currentTarget as HTMLAnchorElement).style.background = t.hover; }}
            onMouseLeave={e => { if (!on) (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
          >
            {tr(labelKey)}
          </AppLink>
        );
      })}
    </div>
  );
}
